function isMultipleSelectionState(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isCheckboxSelectionState(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function getSelectedKeys(value, selectionMode) {
  if (selectionMode === 'single') {
    return typeof value === 'string' ? [value] : [];
  }
  if (selectionMode === 'multiple') {
    if (!isMultipleSelectionState(value)) {
      return [];
    }
    return Object.entries(value).filter(_ref => {
      let [, state] = _ref;
      return Boolean(state);
    }).map(_ref2 => {
      let [key] = _ref2;
      return key;
    });
  }
  return getCheckedKeys(value);
}
function getCheckedKeys(value) {
  if (!isCheckboxSelectionState(value)) {
    return [];
  }
  return Object.entries(value).filter(_ref3 => {
    let [, state] = _ref3;
    return state.checked;
  }).map(_ref4 => {
    let [key] = _ref4;
    return key;
  });
}
function hasSameCheckedKeys(left, right) {
  const leftCheckedKeys = new Set(getCheckedKeys(left));
  const rightCheckedKeys = new Set(getCheckedKeys(right));
  return leftCheckedKeys.size === rightCheckedKeys.size && [...leftCheckedKeys].every(key => rightCheckedKeys.has(key));
}
function buildSelection(checkedKeys) {
  const selection = {};
  for (const key of checkedKeys) {
    selection[key] = {
      checked: true,
      partialChecked: false
    };
  }
  return selection;
}
function deleteDescendantKeys(selectedKeys, key, childrenByNodeKey) {
  var _a, _b;
  const stack = [...((_a = childrenByNodeKey.get(key)) !== null && _a !== void 0 ? _a : [])];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      continue;
    }
    selectedKeys.delete(current);
    stack.push(...((_b = childrenByNodeKey.get(current)) !== null && _b !== void 0 ? _b : []));
  }
}
function addAncestorsToSelection(checkedKeys, key, parentByNodeKey) {
  let current = parentByNodeKey.get(key);
  while (current) {
    checkedKeys.add(current);
    current = parentByNodeKey.get(current);
  }
}
function canonicalizeAncestorsOnlySelection(value, nodeMaps) {
  const checkedKeys = new Set(getCheckedKeys(value));
  for (const key of [...checkedKeys]) {
    addAncestorsToSelection(checkedKeys, key, nodeMaps.parentByNodeKey);
  }
  return buildSelection(checkedKeys);
}
function buildMultipleSelection(selectedKeys) {
  const selection = {};
  for (const key of selectedKeys) {
    selection[key] = true;
  }
  return selection;
}
function buildCheckboxSelectionState(value) {
  if (!isCheckboxSelectionState(value)) {
    return {};
  }
  const selection = {};
  for (const [key, state] of Object.entries(value)) {
    selection[key] = {
      checked: Boolean(state.checked),
      partialChecked: Boolean(state.partialChecked)
    };
  }
  return selection;
}
function deleteNodeWithDescendantsFromSelection(selection, key, childrenByNodeKey) {
  var _a, _b;
  delete selection[key];
  const stack = [...((_a = childrenByNodeKey.get(key)) !== null && _a !== void 0 ? _a : [])];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      continue;
    }
    delete selection[current];
    stack.push(...((_b = childrenByNodeKey.get(current)) !== null && _b !== void 0 ? _b : []));
  }
}
function updateAncestorSelectionState(selection, key, nodeMaps) {
  var _a;
  let current = nodeMaps.parentByNodeKey.get(key);
  while (current) {
    const childKeys = (_a = nodeMaps.childrenByNodeKey.get(current)) !== null && _a !== void 0 ? _a : [];
    const hasCheckedChild = childKeys.some(childKey => {
      var _a;
      return (_a = selection[childKey]) === null || _a === void 0 ? void 0 : _a.checked;
    });
    const hasPartialChild = childKeys.some(childKey => {
      var _a;
      return (_a = selection[childKey]) === null || _a === void 0 ? void 0 : _a.partialChecked;
    });
    const isCompletelyChecked = childKeys.length > 0 && childKeys.every(childKey => {
      var _a;
      return (_a = selection[childKey]) === null || _a === void 0 ? void 0 : _a.checked;
    });
    if (isCompletelyChecked) {
      selection[current] = {
        checked: true,
        partialChecked: false
      };
    } else if (hasCheckedChild || hasPartialChild) {
      selection[current] = {
        checked: false,
        partialChecked: true
      };
    } else {
      delete selection[current];
    }
    current = nodeMaps.parentByNodeKey.get(current);
  }
}
function removeFromCheckboxSelection(value, key, nodeMaps) {
  const selection = buildCheckboxSelectionState(value);
  deleteNodeWithDescendantsFromSelection(selection, key, nodeMaps.childrenByNodeKey);
  updateAncestorSelectionState(selection, key, nodeMaps);
  return selection;
}
function removeFromMultipleSelection(value, key) {
  const selectedKeys = new Set(getSelectedKeys(value, 'multiple'));
  selectedKeys.delete(key);
  return buildMultipleSelection(selectedKeys);
}
function removeSelectedValue(_ref5) {
  let {
    key,
    nodeMaps,
    selectionMode,
    value,
    preserveAncestorsOnlySelection = false
  } = _ref5;
  if (selectionMode === 'multiple') {
    return removeFromMultipleSelection(value, key);
  }
  if (selectionMode !== 'checkbox') {
    return value;
  }
  if (preserveAncestorsOnlySelection) {
    const checkedKeys = new Set(getCheckedKeys(value));
    checkedKeys.delete(key);
    deleteDescendantKeys(checkedKeys, key, nodeMaps.childrenByNodeKey);
    return buildSelection(checkedKeys);
  }
  return removeFromCheckboxSelection(value, key, nodeMaps);
}
function getSelectedNodes(value, selectionMode, nodeMaps) {
  if (!nodeMaps) {
    return [];
  }
  const selectedKeys = new Set(getSelectedKeys(value, selectionMode));
  return nodeMaps.orderedNodeKeys.filter(key => selectedKeys.has(key)).map(key => nodeMaps.nodeByNodeKey.get(key)).filter(node => Boolean(node));
}
function normalizeAncestorsOnlySelection(_ref6) {
  let {
    nextValue,
    previousValue,
    nodeMaps,
    interaction
  } = _ref6;
  if (!interaction) {
    return canonicalizeAncestorsOnlySelection(nextValue, nodeMaps);
  }
  const checkedKeys = new Set(getCheckedKeys(previousValue));
  if (interaction.action === 'select') {
    checkedKeys.add(interaction.key);
    addAncestorsToSelection(checkedKeys, interaction.key, nodeMaps.parentByNodeKey);
    return buildSelection(checkedKeys);
  }
  checkedKeys.delete(interaction.key);
  deleteDescendantKeys(checkedKeys, interaction.key, nodeMaps.childrenByNodeKey);
  return buildSelection(checkedKeys);
}
function buildNodeMaps(nodes) {
  const parentByNodeKey = new Map();
  const childrenByNodeKey = new Map();
  const nodeByNodeKey = new Map();
  const orderedNodeKeys = [];
  const walk = (node, parent) => {
    var _a, _b;
    const key = String(node.key);
    if (parent) {
      parentByNodeKey.set(key, parent);
    }
    nodeByNodeKey.set(key, node);
    orderedNodeKeys.push(key);
    childrenByNodeKey.set(key, ((_a = node.children) !== null && _a !== void 0 ? _a : []).map(child => String(child.key)));
    for (const child of (_b = node.children) !== null && _b !== void 0 ? _b : []) {
      walk(child, key);
    }
  };
  for (const node of nodes !== null && nodes !== void 0 ? nodes : []) {
    walk(node);
  }
  return {
    parentByNodeKey,
    childrenByNodeKey,
    nodeByNodeKey,
    orderedNodeKeys
  };
}

export { buildNodeMaps, canonicalizeAncestorsOnlySelection, getCheckedKeys, getSelectedKeys, getSelectedNodes, hasSameCheckedKeys, normalizeAncestorsOnlySelection, removeSelectedValue };
